import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import { useAppSelector } from '@app/store/hooks';
import type { DialPadSubmitPayload } from '@shared/ui/dial-pad';
import { selectAuthSipAor } from '@entities/auth/model/selectors';

import { findDialPadSuggestions, resolveDialPadTarget } from '../lib/dial-pad-target';
import { selectAllCalls } from '../model/selectors';

const DIAL_PAD_SEARCH_DEBOUNCE_MS = 250;
const DIAL_PAD_SUGGESTIONS_LIMIT = 10;

/** Локальный поиск для DialPad с debounce. */
export const useDialPadSearch = (onSubmitResolved?: (payload: DialPadSubmitPayload) => void) => {
	const allCalls = useAppSelector(selectAllCalls);
	const selfSipAor = useAppSelector(selectAuthSipAor);
	const resolveTarget = useCallback(
		(rawInput: string) =>
			resolveDialPadTarget({
				rawInput,
				lookupCalls: allCalls,
				selfSipAor,
			}),
		[allCalls, selfSipAor]
	);
	const [query, setQuery] = useState('');
	const [debouncedQuery, setDebouncedQuery] = useState('');

	useDebounce(
		() => {
			setDebouncedQuery(query);
		},
		DIAL_PAD_SEARCH_DEBOUNCE_MS,
		[query]
	);

	const onQueryChange = useCallback((nextQuery: string) => {
		setQuery(nextQuery);
	}, []);

	const suggestions = useMemo(
		() =>
			findDialPadSuggestions({
				query: debouncedQuery,
				lookupCalls: allCalls,
				limit: DIAL_PAD_SUGGESTIONS_LIMIT,
			}),
		[allCalls, debouncedQuery]
	);

	const onSubmit = useCallback(
		({ aor: rawInput, withVideo = false }: DialPadSubmitPayload) => {
			const target = resolveTarget(rawInput);
			if (!target.aor) return;

			onSubmitResolved?.({
				aor: target.aor,
				displayName: target.displayName || '',
				withVideo,
			});
		},
		[onSubmitResolved, resolveTarget]
	);

	return {
		suggestions,
		onQueryChange,
		onSubmit,
	};
};
